// Admin card: generate/monitor the AI 3D models (TRELLIS image-to-3D) that the
// floor planner's 3D view renders for each Makasa pack item. Needed once per
// environment — object storage is per-environment, so run it on live too.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Box, RefreshCw } from "lucide-react";
import api from "@/lib/api";

export default function AdminFloorModels() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/floor-planner/models-status");
      setStatus(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    let t;
    const tick = async () => {
      const s = await load();
      if (s?.running) t = setTimeout(tick, 8000);
    };
    tick();
    return () => clearTimeout(t);
  }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/floor-planner/generate-models");
      toast.success("Génération 3D lancée — ~1 min par meuble, en arrière-plan");
      const tick = async () => {
        const s = await load();
        if (s?.running) setTimeout(tick, 8000);
      };
      setTimeout(tick, 8000);
    } catch { toast.error("Lancement impossible"); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      await api.post("/admin/floor-planner/sync-models");
      toast.success("Copie des modèles depuis le preview — quelques secondes, sans coût");
      setTimeout(load, 12000);
    } catch { toast.error("Copie impossible"); }
    finally { setBusy(false); }
  };

  const done = status?.by_status?.done || 0;
  const total = status?.total_items || 30;
  const errors = (status?.items || []).filter((i) => i.status === "error");

  return (
    <div className="border rounded-lg p-5 bg-card" data-testid="floor-models-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold flex items-center gap-2"><Box className="w-4 h-4" /> Floor Planner — Modèles 3D réalistes</h3>
        <div className="flex items-center gap-2">
          <button onClick={sync} disabled={busy || status?.running} data-testid="sync-3d-models-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border rounded disabled:opacity-50"
            title="Copie les modèles déjà générés en preview — gratuit et instantané">
            Copier depuis preview
          </button>
          <button onClick={generate} disabled={busy || status?.running} data-testid="generate-3d-models-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${status?.running ? "animate-spin" : ""}`} />
            {status?.running ? "Génération en cours…" : done >= total ? "Régénérer les manquants" : "Générer les modèles 3D"}
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Transforme chaque photo produit Makasa en vrai modèle 3D texturé (vue 3D du planificateur). ~1 min par meuble, facturé à la clé universelle. À relancer après chaque déploiement si les modèles manquent sur le site live.
      </p>
      <p className="text-sm" data-testid="floor-models-status">
        {status ? <><strong>{done}/{total}</strong> modèles prêts{status.by_status?.error ? ` · ${status.by_status.error} en erreur` : ""}{status.running ? " · génération en cours" : ""}</> : "Chargement…"}
      </p>
      {errors.length > 0 && (
        <ul className="mt-2 text-xs text-red-600 space-y-1">
          {errors.map((e) => <li key={e.id}>{e.id} — {e.error}</li>)}
        </ul>
      )}
    </div>
  );
}
